import { useSceneStore } from '../store/useSceneStore';
import { useTourStore } from '../store/useTourStore';
import { useNarrationStore } from '../voiceover/useNarrationStore';

/**
 * keybindings.ts — Plan 07-03 Task 2 (FALL-04 + UI-05 closure).
 *
 * Single source of truth for the 6-key UI keybinding table. KeyboardHelp.tsx
 * consumes this for the help-overlay rendering AND for the global keydown
 * dispatcher, so collisions are impossible by construction.
 *
 * Keys are BARE (no Ctrl/Alt/Meta) per 07-RESEARCH.md Open Question 4 —
 * collision-free with browser chrome and predictable for keyboard users.
 *
 * The ? binding is a DISPATCHER-owned no-op at the table level; its toggle
 * effect is applied in KeyboardHelp's useEffect so the overlay's useState
 * drives the show/hide behavior (a stateful ref would couple this module to
 * React which is undesirable for a pure data table).
 *
 * Pitfall 4 contract: NOTHING in this module calls preventDefault or touches
 * the Escape key. Escape is owned by the browser's native Pointer Lock API
 * contract (07-RESEARCH.md) and by KeyboardHelp.tsx's explicit close-when-
 * !pointerLock handling.
 */
export interface Keybinding {
  /** Canonical character the binding matches against event.key after uppercase
   *  normalization (`?` is a special case — see matchKeybinding). */
  key: string;
  /** As-shown label in the help overlay (monospace). */
  label: string;
  /** Human-readable description for the help overlay. */
  description: string;
  /** Runs when the key is pressed. Receives the NavSend dispatcher so bindings
   *  that drive the XState machine (R=REBOARD) can route through it without
   *  importing the machine module. */
  action: (send: (event: { type: string }) => void) => void;
}

const KEYBINDINGS_MUTABLE: Keybinding[] = [
  {
    key: '?',
    label: '?',
    description: 'Open or close this keyboard help',
    // The '?' action is owned by the KeyboardHelp component's useState toggle;
    // this function is a no-op so the table can stay pure data.
    action: () => {
      /* toggled in KeyboardHelp.tsx */
    },
  },
  {
    key: 'E',
    label: 'E',
    description: 'Open source info for the structure at your crosshair',
    action: () => {
      // Nearest-mesh selection is driven by a separate crosshair-raycaster
      // (SelectionController in Phase 4). If a crosshair target has been
      // recorded on the scene store, commit it as the selection. No-op
      // otherwise — the Sources panel stays in whatever state it was in.
      const state = useSceneStore.getState();
      const target = (state as unknown as { crosshairTargetManifestId?: string })
        .crosshairTargetManifestId;
      if (typeof target === 'string' && target.length > 0) {
        state.setSelectedObjectId(target);
      }
    },
  },
  {
    key: 'T',
    label: 'T',
    description: 'Toggle 1925 / 2026 era (only inside a vista zone)',
    action: () => {
      const s = useSceneStore.getState();
      if (!s.currentVistaId) return;
      s.setEra(s.era === '1925' ? '2026' : '1925');
    },
  },
  {
    key: 'R',
    label: 'R',
    description: 'Return to the streetcar boarding platform',
    action: (send) => send({ type: 'REBOARD' }),
  },
  {
    key: 'M',
    label: 'M',
    description: 'Open the tour menu',
    action: () => {
      // Toggle the grand tour — if a tour is already active, clear it so M
      // functions as "close the menu" for keyboard users; else start the
      // grand tour. Plan 07-06 may refine into a menu-opens-picker
      // experience; this is the minimal keyboard path for FALL-04 closure.
      const s = useTourStore.getState();
      if (s.activeTourId) s.clearTour();
      else s.setActiveTour('grand-tour-1925');
    },
  },
  {
    key: 'V',
    label: 'V',
    description: 'Mute or unmute narration',
    action: () => {
      const s = useNarrationStore.getState();
      s.setMuted(!s.muted);
    },
  },
];

export const KEYBINDINGS: readonly Keybinding[] = Object.freeze(KEYBINDINGS_MUTABLE);

/**
 * matchKeybinding — resolves a KeyboardEvent to a Keybinding.
 *
 * Returns undefined if:
 *   - Ctrl/Alt/Meta modifier is held (combos are reserved for browser chrome)
 *   - No binding matches the normalized key
 *
 * Special-case: '?' on US keyboards is Shift+/; other layouts produce '?'
 * directly. Both paths must resolve to the '?' binding.
 *
 * Note: Shift+letter is NOT a modifier combo for our purposes — Shift just
 * uppercases the key, which we normalize via toUpperCase().
 */
export function matchKeybinding(event: KeyboardEvent): Keybinding | undefined {
  if (event.ctrlKey || event.metaKey || event.altKey) return undefined;
  // '?' detection — event.key === '?' directly, OR '/' with shiftKey held
  if (event.key === '?' || (event.key === '/' && event.shiftKey)) {
    return KEYBINDINGS.find((b) => b.key === '?');
  }
  const upper = event.key.toUpperCase();
  return KEYBINDINGS.find((b) => b.key === upper);
}
